import React, { useCallback, useEffect, useState } from 'react';
import { logger } from '@common';
import { useMessagingContext } from '@frontend/hooks';
import { SortingState } from '@tanstack/react-table';
import { PostTypeInsights, GetSocialAnalyticsNetworksQuery } from '@frontend/gql/social/graphql';
import { TABLE_PAGE_SIZE } from '@frontend/app/constants';
import { useGetTableConfigurationByName, useSaveTableConfigurationMutation } from '@frontend/app/hooks';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Card } from '@frontend/shadcn/components/ui/card';
import { ColumnsIconV3 } from '@frontend/app/components/Icons';
import { ArrowDownToLineIcon } from '@revfluence/fresh-icons/solid';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import MembersCard from '../members-card';
import ReachCard from '../reach-card';
import EngagementCard from '../engagement-card';
import ImpressionsCard from '../impressions-card';
import PostsCard from '../post-card';
import TMVCard from '../tmv-card';
import { InstagramMetricsCard } from '../instagram-metrics-card';
import { TikTokMetricsCard } from '../tiktok-metrics-card';
import { YouTubeMetricsCard } from '../youtube-metrics-card';
import { PinterestMetricsCard } from '../pinterest-metrics-card';
import { useNetworksTable } from '../table/hooks/useNetworksTable';
import {
  TableHeader,
  TableHeaderSection,
  TableHeaderLayout,
  TableHeaderLabel,
  TableHeaderIconButton,
} from '../table-header';
import { Spinner } from '../ui/spinner';
import ColumnDialog from '../columnSelectorDialog';
import NetworksTable from '../table/networks-table';
import { NETWORKS_TABLE_COLUMN_CONFIG_VERSION, NetworksTableColumn, hydrateColumnsFromJson } from '../table/config/defaultNetworksTableColumns';
import { useColumnDialog } from '../columnSelectorDialog/hooks/useColumnDialog';
import { defaultNetworksTableColumns } from '../table/config/defaultNetworksTableColumns';
import { useExportNetworksCsv } from '../../hooks/useExportNetworksCsv';

interface NetworksTabProps {
  data: GetSocialAnalyticsNetworksQuery | undefined;
  isLoading: boolean;
  isPartialLoading: boolean;
  selectedColumns: NetworksTableColumn<PostTypeInsights>[];
  setCurrentPage: (page: number) => void;
  currentPage: number;
  setSelectedColumns: (columns: NetworksTableColumn<PostTypeInsights>[]) => void;
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
}

export const NetworksTab = ({
  data,
  isLoading,
  isPartialLoading,
  currentPage,
  selectedColumns,
  setSelectedColumns,
  sorting,
  setSorting,
}: NetworksTabProps) => {
  const totalInsights = data?.client?.socialAnalytics?.totalInsights;
  const postTypes = data?.client?.socialAnalytics?.postTypes || [];

  const { handleExportCsv, isExporting } = useExportNetworksCsv({
    postTypes,
    selectedColumns,
  });

  const [editModeColumns, setEditModeColumns] = useState<NetworksTableColumn<PostTypeInsights>[]>(selectedColumns);
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();
  const [saveTableConfiguration] = useSaveTableConfigurationMutation();

  const { data: tableConfigData, loading: tableConfigLoading } = useGetTableConfigurationByName({
    variables: { tableName: 'social-data-networks-table' },
  });

  const onSaveColumnInfo = useCallback(
    async (columns: NetworksTableColumn<PostTypeInsights>[]) => {
      try {
        await saveTableConfiguration({
          variables: {
            data: {
              version: NETWORKS_TABLE_COLUMN_CONFIG_VERSION,
              tableName: 'social-data-networks-table',
              columnDefinitions: columns,
            },
          },
        });
        setSelectedColumns(columns);
        setEditModeColumns(columns);
        showSuccessMessage('Columns successfully updated');
      } catch (error) {
        logger.error(error);
        showErrorMessage('error saving columns');
      }
    },
    [saveTableConfiguration, showSuccessMessage, showErrorMessage, setSelectedColumns],
  );

  useEffect(() => {
    if (
      !tableConfigLoading &&
      tableConfigData?.getTableConfigurationByName?.columnDefinitions &&
      tableConfigData?.getTableConfigurationByName?.version === NETWORKS_TABLE_COLUMN_CONFIG_VERSION
    ) {
      const newColumns = hydrateColumnsFromJson(tableConfigData.getTableConfigurationByName.columnDefinitions);
      setSelectedColumns(newColumns);
      setEditModeColumns(newColumns);
    }
  }, [tableConfigLoading, tableConfigData, setSelectedColumns]);

  const { postTypeTableProps, metricsCardProps } = useNetworksTable({
    selectedColumns,
    setSelectedColumns,
    data,
    currentPage,
    pageSize: TABLE_PAGE_SIZE,
    handleSortingChange: setSorting,
    sorting,
  });

  const { columnDialogProps } = useColumnDialog({
    defaultSelectedColumns: defaultNetworksTableColumns,
    selectedColumns: editModeColumns,
    setSelectedColumns: setEditModeColumns,
    onSave: onSaveColumnInfo,
  });

  const isAnyExporting = isExporting;
  const handleExport = async (format: 'current' | 'full') => {
    try {
      if (format === 'current') {
        handleExportCsv();
        showSuccessMessage('Current view exported successfully');
      } else {
        handleExportCsv(true);
        showSuccessMessage('Full data exported successfully');
      }
    } catch (error) {
      showErrorMessage('Failed to export data');
      logger.error('Export failed:', error);
    }
  };

  if (isLoading && !isPartialLoading) {
    return (
      <>
        <section className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4" aria-busy>
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading posts card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading members card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading impressions card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading engagement card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading reach card" />
          <Skeleton className="h-[110px] rounded-xl" aria-label="Loading TMV card" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 p-4">
          <Skeleton className="h-[400px] rounded-xl" aria-label="Loading Instagram metrics card" />
          <Skeleton className="h-[400px] rounded-xl" aria-label="Loading TikTok metrics card" />
          <Skeleton className="h-[400px] rounded-xl" aria-label="Loading YouTube metrics card" />
          <Skeleton className="h-[400px] rounded-xl" aria-label="Loading Pinterest metrics card" />
        </section>

        <Skeleton className="mx-4 mb-4 rounded-xl" aria-label="Loading members table">
          <div className="p-4">
            <Skeleton className="h-[60px] w-full rounded-lg pb-4" />
            <div className="space-y-2 pt-4">
              {Array.from({ length: 10 }, (_, i) => (
                <Skeleton key={`skeleton-row-${i}-${i}`} className="h-[48px] w-full rounded-lg" />
              ))}
            </div>
          </div>
        </Skeleton>
      </>
    );
  }

  return (
    <>
      <section className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4">
        <PostsCard data={totalInsights} />
        <MembersCard data={totalInsights} />
        <ImpressionsCard data={totalInsights} />
        <EngagementCard data={totalInsights} />
        <ReachCard data={totalInsights} />
        <TMVCard data={totalInsights} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 p-4">
        <InstagramMetricsCard {...metricsCardProps.instagram} />
        <TikTokMetricsCard {...metricsCardProps.tiktok} />
        <YouTubeMetricsCard {...metricsCardProps.youtube} />
        <PinterestMetricsCard {...metricsCardProps.pinterest} />
      </section>

      <Card className="mx-4 mb-4" aria-label="Networks table">
        <TableHeader>
          <TableHeaderLayout>
            <TableHeaderSection>
              <TableHeaderLabel>
                <div className="flex items-center gap-2">
                  <span aria-live="polite">{postTypeTableProps?.data?.length} Post Types</span>
                  {isPartialLoading && <Spinner size="sm" spinning={isPartialLoading} />}
                </div>
              </TableHeaderLabel>
            </TableHeaderSection>
            <TableHeaderSection>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <TableHeaderIconButton
                    disabled={isAnyExporting || postTypes.length === 0}
                    data-dd-action-name="export-posts-data"
                    aria-label={isAnyExporting ? 'Export in progress...' : 'Export member data'}
                    aria-busy={isAnyExporting || postTypes.length === 0}
                  >
                    <div className="flex items-center">
                      <ArrowDownToLineIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                  </TableHeaderIconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => handleExport('current')}
                    disabled={isAnyExporting || postTypes.length === 0}
                    data-dd-action-name="export-current-view-networks-post-types"
                    aria-label="Export current view of networks post types data"
                    aria-busy={isAnyExporting || postTypes.length === 0}
                    className="cursor-pointer"
                  >
                    Export current view
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleExport('full')}
                    disabled={isAnyExporting || postTypes.length === 0}
                    data-dd-action-name="export-full-data-networks-post-types"
                    aria-label="Export all data fields"
                    aria-busy={isAnyExporting || postTypes.length === 0}
                    className="cursor-pointer"
                  >
                    Export all fields
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ColumnDialog {...columnDialogProps}>
                <TableHeaderIconButton
                  data-dd-action-name="open-column-selector"
                  aria-label="Select columns to display"
                >
                  <ColumnsIconV3 size={16} aria-hidden="true" />
                </TableHeaderIconButton>
              </ColumnDialog>
            </TableHeaderSection>
          </TableHeaderLayout>
        </TableHeader>
        <NetworksTable {...postTypeTableProps} />
      </Card>
    </>
  );
};
